import { Action, getModule, Module, Mutation, VuexModule } from 'vuex-module-decorators';
import { initialize, LDClient, LDUser } from 'launchdarkly-js-client-sdk';
import { CurrentBanner } from 'types/banner';
import { CurrentModal } from 'types/modal';
import { getDistinctId } from '@utils/analytics';
import InvestorUpdateBanner from '@components/layout/investor-update-banner.vue';
import { Route } from 'vue-router';
import { RouteTransition } from 'types/layout';
import { ScreenSizeType } from 'types/client';
import store from '..';
import { Toast } from 'types/layout';
import { verifyPassword } from '@api/user';

export interface AppState {
	currentModal: CurrentModal | null;
	isAuthenticated: boolean;
	screenSize: ScreenSizeType;
	oauthToken: string;
	toasts: Array<Toast>;
}

@Module({
	dynamic: true,
	namespaced: true,
	name: 'app',
	store
})
class AppModule extends VuexModule {
	currentModal: CurrentModal | null = null;
	currentBanner: CurrentBanner | null = null;

	ldClient: LDClient | null = null;

	isAuthenticated = false;

	oauthToken = `${window.fundriseApp.oAuthToken}`;
	tokenScope = 'UNAUTHENTICATED';

	routeTransition: RouteTransition = {
		scope: 'fullpage',
		name: 'fade'
	};

	screenSize: ScreenSizeType = 'DESKTOP';

	subPageHeight: number | null = null;

	toasts: Array<Toast> = window.fundriseApp.initialToasts;

	@Action({ rawError: true })
	public async isFeatureEnabled(featureFlag: string): Promise<boolean> {
		if (this.ldClient) {
			await this.ldClient.waitUntilReady();
			return this.ldClient.variation(featureFlag, false) as boolean;
		}

		return false;
	}

	@Action({ rawError: true })
	public async initializeLaunchDarklyInstance(): Promise<void> {
		const mixpanelId = getDistinctId();
		const clientId = `${process.env.VUE_APP_LAUNCH_DARKLY_CLIENT_ID}`;
		const ldUser: LDUser = { key: mixpanelId, custom: { client_type: 'web' } };

		if (mixpanelId) {
			const ldClient = initialize(clientId, ldUser);
			this.SET_LD_INSTANCE(ldClient);
		}
	}

	@Action({ rawError: true })
	public async getCurrentBanner(routeKey = ''): Promise<void> {
		const hideBannerList = [
			'/acq-plus/start',
			'/partner',
			'/webview/',
			'/story/',
			'/data-visualizations/',
			'/fundrise-ipo/mobile-view',
			'/reits/26/view',
			'/offerings/26/view',
			'/fundriseintervalfund-iframe-only/24',
			'/fundriseintervalfund-iframe-only/25',
			'/fundriseintervalfund-iframe-only/26'
		];
		const hideBanner = hideBannerList.filter((hideBannerPath) => {
			return routeKey.includes(hideBannerPath);
		});

		if (hideBanner.length > 0) {
			this.UPDATE_CURRENT_BANNER(null);
		} else {
			this.UPDATE_CURRENT_BANNER({
				banner: InvestorUpdateBanner
			});
		}
	}

	@Action({ rawError: true })
	public async getPrivilegedToken(password: string): Promise<void> {
		const accessToken = await verifyPassword(password);
		this.SET_OAUTH_TOKEN(accessToken);
	}

	@Mutation
	public SET_IS_AUTHENTICATED(isAuthenticated: boolean): void {
		this.isAuthenticated = isAuthenticated;
	}

	@Mutation
	public UPDATE_CURRENT_MODAL(currentModal: CurrentModal | null): void {
		this.currentModal = currentModal;
	}

	@Mutation
	public UPDATE_CURRENT_BANNER(currentBanner: CurrentBanner | null): void {
		this.currentBanner = currentBanner;
	}

	@Mutation
	public SET_OAUTH_TOKEN(token: string): void {
		this.oauthToken = token;
		window.fundriseApp.oAuthToken = token;
	}

	@Mutation
	public SET_LD_INSTANCE(ldClient: LDClient) {
		this.ldClient = ldClient;
	}

	@Mutation
	public ADD_TOAST(toastToAdd: Toast): void {
		const newToast = this.toasts.filter((toast) => toast.message === toastToAdd.message).length <= 0;

		if (newToast) {
			this.toasts.push(toastToAdd);
		}
	}

	@Mutation
	public DISMISS_TOAST(dismissed: Toast): void {
		this.toasts = this.toasts.filter((toast) => toast.message !== dismissed.message);
	}

	@Mutation
	public UPDATE_ROUTE_TRANSITION(payload: { to: Route; from: Route }): void {
		const toStepIndex =
			typeof payload?.to?.meta?.step === 'function'
				? payload.to.meta.step(payload.to)
				: typeof payload?.to?.meta?.step === 'number'
				? payload.to.meta.step
				: undefined;
		const fromStepIndex =
			typeof payload?.from?.meta?.step === 'function'
				? payload.from.meta.step(payload.from)
				: typeof payload?.from?.meta?.step === 'number'
				? payload.from.meta.step
				: undefined;

		if (toStepIndex && fromStepIndex) {
			this.routeTransition.scope = 'subpage';
			if (fromStepIndex < toStepIndex) {
				this.routeTransition.name = 'slide-left';
			} else if (fromStepIndex > toStepIndex) {
				this.routeTransition.name = 'slide-right';
			} else {
				this.routeTransition.name = 'fade';
			}
		} else {
			this.routeTransition.scope = 'fullpage';
			this.routeTransition.name = 'fade';
		}
	}

	@Mutation
	public UPDATE_SCREEN_SIZE(screenSize: ScreenSizeType): void {
		if (screenSize !== this.screenSize) {
			this.screenSize = screenSize;
		}
	}

	@Mutation
	public UPDATE_SUB_PAGE_HEIGHT(height: number): void {
		if (height !== this.subPageHeight) {
			this.subPageHeight = height;
		}
	}

	@Mutation
	public UPDATE_TOKEN_SCOPE(scope: string): void {
		this.tokenScope = scope;
	}
}

export const app = getModule(AppModule);
